import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { getBackgroundTheme, getButtonTheme, type } from '../../../../style';
import { auth10tixVerification, WYP_PRIVATE_IMG_FALLBACK } from '../../../../config/Master';
import DashboardDiscoverModeMediaSlider from './DashboardDiscoverModeMediaSlider';
import DashboardDiscoverModeMediaCounter from './DashboardDiscoverModeMediaCounter';
import { useDashboardDiscoverMode } from '../../../../contexts/DashboardDiscoverModeContext';
import { Account } from '../../../../models/Account';
import useMixPanel from '../../../../hooks/useMixPanel';
import { EVENT_NAMES } from '../../../../constants/mixpanel';
import { PhotoAspectRatioProps } from './types/DashboardDiscoverModeProfileCard';
import { useLocation } from 'react-router-dom';
import breakpoints from '../../../../theme/material/breakpoints';
import { MEDIA_SCREEN_HEIGHT_300_450 } from '../styles/media';
import VerifiedBadgeTooltip from '../../../common/VerifiedBadgeTooltip';
import { FONT_FAMILIES } from 'theme/material/constants';

const CardContainer = styled('div')({
  position: 'relative',
  width: 'calc(100vw - 20px)',
  borderRadius: 10,
  overflow: 'hidden',
  [breakpoints.up(769)]: {
    width: 348,
  },
  [breakpoints.between(600, 768)]: {
    [MEDIA_SCREEN_HEIGHT_300_450]: {
      width: 348,
    },
  },
});

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const PhotoAspectRatio = styled('div')(({ paddingTop }: PhotoAspectRatioProps) => ({
  paddingTop: paddingTop || '133%',
  position: 'relative',
  [breakpoints.up(769)]: {
    paddingTop: 465,
  },
  [breakpoints.between(600, 768)]: {
    [MEDIA_SCREEN_HEIGHT_300_450]: {
      paddingTop: 465,
    },
  },
}));

const AccountDetailsContainer = styled('div')({
  color: 'white',
  position: 'absolute',
  left: '0px',
  bottom: '0px',
  width: '100%',
  zIndex: 3,
  overflow: 'hidden',
});

const AccountDetails = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'bottom',
  paddingLeft: '12px',
  paddingBottom: '1px',
  flexWrap: 'wrap',
  width: '75%',
  textOverflow: 'ellipsis',
  textAlign: 'left',
});

const UsernameContainer = styled('div')(() => ({
  ...type.title.XS,
  fontWeight: 400,
  letterSpacing: '-0.5px',
  width: '100%',
  color: getButtonTheme().GreyAndWhite,
}));

const Username = styled('div')({
  ...type.LG,
  width: 'auto',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: 'inline-block',
  maxWidth: '80%',
  fontFamily: FONT_FAMILIES.secondary,
  [breakpoints.down(768.05)]: {
    ...type.title.XS,
  },
});

const UserDetails = styled('div')(() => ({
  ...type.XS,
  fontWeight: 400,
  color: getBackgroundTheme().cardDetails,
  width: '85%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const Gradient = styled('div')({
  background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, #000 100%)',
  position: 'absolute',
  width: '100%',
  height: 74,
  bottom: '-1px',
  left: 0,
  zIndex: 2,
});

const OnlineDot = styled('div')({
  height: '8px',
  width: '8px',
  backgroundColor: 'lightgreen',
  borderRadius: '50%',
  display: 'inline-block',
});

const VerifiedBadge = styled('span')({
  position: 'relative',
  top: 8,
  verticalAlign: 'top',
  marginLeft: 6,
  zIndex: 2,
  [breakpoints.down(768.05)]: {
    top: 6,
  },
});

const BackgroundOverlay = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.1), rgba(0, 0, 0, 0.5))',
  zIndex: 1,
});

interface CardProps {
  profile: any;
  account: Account;
}

const DashboardDiscoverModeProfileCard = ({ profile, account }: CardProps): JSX.Element => {
  const { pathname } = useLocation();
  const { setMixpanelDataByKeyHandler } = useMixPanel();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [photosViewedCount, setPhotosViewedCount] = useState(0);
  const {
    photoAspectRatioRef,
    offerActionContainerRef,
    discoverActiveAccount,
    setAnimateDirection,
    setIsDotsAnimated,
    calculateHeightPhotoSlider,
    discoverCardMediaRatio,
  } = useDashboardDiscoverMode();

  const mergePhotosVideos = [
    ...account.data.photo.data.filter((item: { status: string }) => item.status === 'approved'),
    ...account.data.video.data.filter((item: { status: string }) => item.status === 'approved'),
  ].filter(Boolean);

  const getPrivatePhotos = (): Array<any> => {
    //bypass private data
    if (profile?.private_photos_count && profile?.private_photos_count > 0) {
      return [
        {
          type: 'photo',
          id: 0,
          urls: {
            '820px': `${WYP_PRIVATE_IMG_FALLBACK}`,
          },
        },
      ];
    }

    return photosApprovedPrivate.length > 0 ? [photosApprovedPrivate[0]] : [];
  };

  const isVerified = account.data.id_verification?.data?.status === 'approved';
  const isSelfieVerified = account?.data?.profile?.data?.selfie_verified;

  const photosApprovedPublic = mergePhotosVideos.filter(item => item.id);
  const photosApprovedPrivate = mergePhotosVideos.filter(item => !item.id);
  const privatePhotos = getPrivatePhotos();
  const photosPublicWithOnePrivate = [...photosApprovedPublic, ...privatePhotos].filter(Boolean);

  useEffect(() => {
    if (photosViewedCount > 0) {
      setMixpanelDataByKeyHandler({
        key: EVENT_NAMES.DISCOVER_MODE_ACTION,
        payload: { 'Number of Photos Viewed': photosViewedCount + 1 },
      });
    }
  }, [photosViewedCount]);

  const handleSlide = (step: number) => {
    const nextSlide = currentSlide + step;

    if (nextSlide >= 0 && nextSlide < photosPublicWithOnePrivate.length) {
      setCurrentSlide(nextSlide);
      setPhotosViewedCount(prevState => (prevState < nextSlide ? prevState + 1 : prevState));
      setAnimateDirection(nextSlide > currentSlide ? 'right' : 'left');

      if (nextSlide === 0) {
        setIsDotsAnimated(false);
      }

      setIsDotsAnimated(true);
    } else {
      setIsDotsAnimated(false);
    }
  };

  useEffect(() => {
    calculateHeightPhotoSlider();
  }, [offerActionContainerRef?.current, photoAspectRatioRef?.current]);

  useEffect(() => {
    const cardHeightTimeout = setTimeout(() => {
      calculateHeightPhotoSlider();
    }, 300);

    return () => {
      clearTimeout(cardHeightTimeout);
    };
  }, [pathname]);

  return (
    <CardContainer key={account?.data?.hash_id} className="card">
      {discoverActiveAccount?.hash_id !== account.data.hash_id && <BackgroundOverlay />}
      <PhotoAspectRatio
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        paddingTop={discoverCardMediaRatio}
        ref={photoAspectRatioRef}
      >
        <AccountDetailsContainer>
          <AccountDetails>
            <UsernameContainer>
              <Username>{account.data.username}</Username>
              {((auth10tixVerification && isVerified) || isSelfieVerified) && (
                <VerifiedBadge data-test-id="data-verified-badge">
                  <VerifiedBadgeTooltip
                    showToolTip={true}
                    isIdVerified={isVerified}
                    isSelfieVerified={isSelfieVerified}
                  />
                </VerifiedBadge>
              )}
            </UsernameContainer>
            <UserDetails data-test-id="data-member-age-city">
              {profile.online ? <OnlineDot /> : ''} {profile.age} {profile.city && <>&middot; {profile.city}</>}
            </UserDetails>
          </AccountDetails>
        </AccountDetailsContainer>
        <Gradient />
        <DashboardDiscoverModeMediaSlider
          photos={photosPublicWithOnePrivate}
          profile={profile}
          currentSlide={currentSlide}
          handleSlide={handleSlide}
          privateCount={profile?.private_photos_count ?? photosApprovedPrivate.length}
        />
      </PhotoAspectRatio>
      <DashboardDiscoverModeMediaCounter
        key={discoverActiveAccount?.hash_id}
        photos={photosPublicWithOnePrivate}
        currentSlide={currentSlide}
        hashId={account.data.hash_id}
      />
    </CardContainer>
  );
};

export default DashboardDiscoverModeProfileCard;
